<template>
  <div>
    <v-file-input
        label="Upload Profile Photo"
        accept="image/png, image/jpeg"
        :error-messages="errorMessage"
        :rules="[fileRules]"
        v-model="rawFile"
        show-size
        prepend-icon="mdi-camera"
        class="mb-2"
        variant="outlined"
        density="compact"
    />

    <!-- Cropped Avatar Preview -->
    <v-avatar
        v-if="croppedUrl"
        size="128"
        class="mt-2 mb-4 mx-auto"
    >
      <v-img :src="croppedUrl" cover />
    </v-avatar>

    <!-- Cropping Dialog -->
    <v-dialog v-model="dialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">Crop Your Photo</v-card-title>
        <v-card-text>
          <cropper
              v-if="imageUrl"
              class="cropper"
              :src="imageUrl"
              :stencil-props="{ aspectRatio: 1 }"
              :auto-zoom="true"
              :resize-image="true"
              @change="onCrop"
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="cancelCrop">Cancel</v-btn>
          <v-btn color="primary" @click="finalizeCrop">Crop & Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useField } from 'vee-validate';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

const props = defineProps<{
  name: string;
}>();

const { value, errorMessage, handleChange } = useField<File | null>(props.name);

const dialog = ref(false);
const rawFile = ref<File | null>(null);
const imageUrl = ref<string | null>(null);
const croppedUrl = ref<string | null>(null);
let cropResult: any = null;

watch(value, (newVal) => {
  if (!newVal) {
    rawFile.value = null;
    imageUrl.value = null;
    croppedUrl.value = null;
    cropResult = null;
    dialog.value = false;
  }
});

watch(rawFile, (file) => {
  if (file && file.size < 2 * 1024 * 1024) {
    const reader = new FileReader();
    reader.onload = (e) => {
      imageUrl.value = e.target?.result as string;
      dialog.value = true; // open cropping dialog
    };
    reader.readAsDataURL(file);
  } else {
    imageUrl.value = null;
    croppedUrl.value = null;
  }
});

function onCrop(result: any) {
  cropResult = result;
}

function finalizeCrop() {
  if (!cropResult?.canvas) return;
  cropResult.canvas.toBlob((blob: Blob | null) => {
    if (blob && rawFile.value) {
      const file = new File([blob], rawFile.value.name, { type: blob.type });
      croppedUrl.value = URL.createObjectURL(blob);
      handleChange(file); // update Vee-Validate
      dialog.value = false;
    }
  }, 'image/jpeg');
}

function cancelCrop() {
  rawFile.value = null;
  imageUrl.value = null;
  croppedUrl.value = null;
  cropResult = null;
  dialog.value = false;
}

function fileRules(file: File) {
  if (!file) return true;
  const validTypes = ['image/jpeg', 'image/png'];
  if (!validTypes.includes(file.type)) return 'Only JPG and PNG images are allowed';
  if (file.size > 2 * 1024 * 1024) return 'Image must be less than 2MB';
  return true;
}
</script>

<style scoped>
.cropper {
  max-width: 100%;
  height: 300px;
  border: 1px solid #ddd;
  margin-top: 10px;
}
</style>
